import type { ReactNode } from 'react'

// Shared primitives for the browser-print documents — Production Plan, Event
// Plan and the all-events report. Print-CSS only: the browser's "Save as PDF"
// renders these, so there's no PDF library or server renderer to maintain.

export function PrintShell({ children }: { children: ReactNode }) {
  return (
    <>
      <style>{`
        @media print {
          @page { size: A4; margin: 12mm; }
          body { background: #fff; }
        }
        .nymr-print-page {
          font-family: 'Source Sans 3', Arial, sans-serif;
          color: #1a1a1a;
          font-size: 12.5px;
          line-height: 1.5;
          max-width: 720px;
          margin: 0 auto;
          padding: 24px;
        }
        .nymr-print-page h1 {
          font-family: 'Playfair Display', Georgia, serif;
          font-size: 26px;
          color: #0a4a3a;
          margin-bottom: 4px;
        }
        .nymr-print-page h2 {
          font-family: 'Playfair Display', Georgia, serif;
          font-size: 18px;
          color: #1a1a1a;
          margin-top: 20px;
          margin-bottom: 8px;
          padding-bottom: 4px;
          border-bottom: 2px solid #9b7f2a;
        }
        .nymr-print-page p { margin-bottom: 6px; }
        .nymr-print-page table {
          width: 100%;
          border-collapse: collapse;
          margin: 8px 0;
          font-size: 11px;
        }
        .nymr-print-page th {
          background: #0a4a3a;
          color: #fff;
          padding: 6px 8px;
          text-align: left;
          font-size: 10px;
          text-transform: uppercase;
          letter-spacing: 0.08em;
        }
        .nymr-print-page td {
          padding: 5px 8px;
          border-bottom: 1px solid #e5e0d4;
          vertical-align: top;
        }
        .nymr-print-page section { page-break-inside: avoid; }
      `}</style>
      <div className="nymr-print-page">{children}</div>
    </>
  )
}

export function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section>
      <h2 dangerouslySetInnerHTML={{ __html: title }} />
      {children}
    </section>
  )
}

export function Table({ headers, children }: { headers: string[]; children: ReactNode }) {
  return (
    <table>
      <thead>
        <tr>{headers.map((h, i) => <th key={i}>{h}</th>)}</tr>
      </thead>
      <tbody>{children}</tbody>
    </table>
  )
}

export function Empty({ children }: { children: ReactNode }) {
  return <p style={{ fontStyle: 'italic', color: '#5a5a5a' }}>{children}</p>
}

export function SignRow({ label }: { label: string }) {
  return (
    <tr>
      <td style={{ width: 160, padding: '14px 0', borderBottom: '1px solid #d8d2c4' }}>
        <strong dangerouslySetInnerHTML={{ __html: label }} />
      </td>
      <td style={{ padding: '14px 0', borderBottom: '1px solid #d8d2c4' }}>
        <span style={{ color: '#5a5a5a', fontSize: 10 }}>Signature:</span>
      </td>
      <td style={{ padding: '14px 0', borderBottom: '1px solid #d8d2c4', width: 100 }}>
        <span style={{ color: '#5a5a5a', fontSize: 10 }}>Date:</span>
      </td>
    </tr>
  )
}

// Auto-print intentionally disabled (Chris's request): the plan/report pages
// just render, and the user saves or prints via the browser's own dialog
// (File ▸ Print / Ctrl+P). Kept as a no-op so existing callers don't change.
export function usePrintOnReady(_ready: boolean) {
  // no-op
}
